import AddListModal from './modal/add_list';
import { BoardListObject } from '../../params/redux/task';
import CardWrapper from '../wrapper/card';
import DeleteListModal from './modal/delete_list';
import { dragTaskListItemThunk } from '../../redux/thunks/task';
import { gray } from '../../theme/themePrimitives';
import TaskListItemComponent from './task_list_item';
import { useParams } from 'react-router';
import { useState } from 'react';
import { Add, MoreVert } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { settingAddingTask, settingSelectedBoardList, settingSelectedBoardTaskDetail } from '../../redux/slices/task';
import { useDispatch, useSelector } from 'react-redux';

const CreatedTaskComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const [deleteModal, setDeleteModal] = useState<number | null>(null);
    const [addModal, setAddModal] = useState<number | null>(null);
    const { tasks } = useSelector((state: RootState) => state.task);
    const { currentUser } = useSelector((state: RootState) => state.app);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | { el: HTMLElement, task: BoardListObject }>(null);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, task: BoardListObject) => {
        setMenuAnchorEl({ el: event.currentTarget, task });
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const onDragEnd = async (result: DropResult) => {
        const { source, destination } = result;
        // If dropped outside a droppable area, do nothing
        if (!destination) return;

        // If dropped in the same place, do nothing
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }

        // Find source and destination list indices
        const sourceListIndex = tasks?.lists.findIndex((list: BoardListObject) => list.id.toString() === source.droppableId);
        const destListIndex = tasks?.lists.findIndex((list: BoardListObject) => list.id.toString() === destination.droppableId);

        if (!sourceListIndex || !destListIndex) return;

        const sourceItems = Array.from(tasks?.lists[sourceListIndex].items || []);
        const [movedItem] = sourceItems.splice(source.index, 1);

        if (!movedItem) return;

        // Dispatch the dragTaskListItemThunk with the dragged item ID and destination list ID
        const data = {
            item_id: movedItem.id,
            list_id: parseInt(destination.droppableId, 10),
            client_id: id ? parseInt(id as string, 10) : 0
        };

        try {
            await dispatch(dragTaskListItemThunk(data)).unwrap();
        } catch (e) {
            // Catch errors
        }
    };

    const addNewItemHandler = (task: BoardListObject) => {
        dispatch(settingAddingTask(true));
        dispatch(settingSelectedBoardList(task));
    };

    const deleteHandler = (task: BoardListObject) => {
        dispatch(settingSelectedBoardList(task));
        setDeleteModal(task.id);
        handleMenuClose();
    };

    const editHandler = (task: BoardListObject) => {
        dispatch(settingSelectedBoardList(task));
        setAddModal(task.id);
        handleMenuClose();
    };

    const onCloseHandler = () => {
        setDeleteModal(null);
        setAddModal(null);
        dispatch(settingSelectedBoardList(null));
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Box display="flex" flex="wrap" gap="10px" mt={2}>
                {tasks?.lists && tasks?.lists.map((task) => (
                    <Droppable droppableId={task.id.toString()} key={task.id}>
                        {(provided) => (
                            <Box
                                minWidth="300px"
                                maxWidth="300px"
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                <CardWrapper>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle2" color="textSecondary" fontWeight="bold">{task.list_name}</Typography>
                                        {task.created_by === currentUser?.id && <IconButton size="small" onClick={(e) => handleMenuClick(e, task)}>
                                            <MoreVert />
                                        </IconButton>}
                                        <Menu
                                            anchorEl={menuAnchorEl?.el}
                                            open={menuAnchorEl?.task?.id === task.id}
                                            onClose={handleMenuClose}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        >
                                            <MenuItem onClick={() => editHandler(task)}>Edit</MenuItem>
                                            <MenuItem onClick={() => deleteHandler(task)}>Delete</MenuItem>
                                        </Menu>
                                    </Box>
                                    <Box mt={2}>
                                        {task.items.map((item, i) => (
                                            <Draggable draggableId={item.id.toString()} index={i} key={item.id}>
                                                {(draggableProvided) => (
                                                    <Box
                                                        ref={draggableProvided.innerRef}
                                                        {...draggableProvided.draggableProps}
                                                        {...draggableProvided.dragHandleProps}
                                                        bgcolor={gray[50]}
                                                        p={1}
                                                        borderRadius="5px"
                                                        mb={2}
                                                    >
                                                        <TaskListItemComponent taskItem={item} />
                                                    </Box>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </Box>
                                </CardWrapper>
                                <Box display="flex" mt={1} justifyContent="center">
                                    <Button variant="text" onClick={() => {
                                        dispatch(settingSelectedBoardTaskDetail(null));
                                        addNewItemHandler(task);
                                    }}><Add fontSize="small" color="info" />Add New Task</Button>
                                </Box>
                                {addModal === task.id && <AddListModal open={addModal === task.id} onClose={onCloseHandler} />}
                                {deleteModal === task.id && <DeleteListModal open={deleteModal === task.id} onClose={onCloseHandler} />}
                            </Box>
                        )}
                    </Droppable>
                ))}
            </Box>
        </DragDropContext>
    );
};

export default CreatedTaskComponent;